/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import React, {useState, useEffect} from 'react';
import classNames from 'classnames';
import styled from 'styled-components';

import PropTypes from 'prop-types';
import { faChevronDown, faChevronRight, faMinusCircle, faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import LinkButton from "./LinkButton";
import tokens from "../styles/design-tokens";
import { APP_COLORS } from "../Constants";

const AccordionWrapper = styled.div`
  & + * {
    margin-top: ${tokens.spacing.sm};
  }
`;

const AccordionTitle = styled.div`
  font-weight: ${tokens.typography.fontWeight.semibold};
  cursor: pointer;
  color: ${props => props.theme?.colors?.primary || tokens.colors.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${tokens.colors['gray-100']};
  box-shadow: ${tokens.shadows.sm};
  
  &.open {
    color: ${props => props.theme?.colors?.primary || tokens.colors.primary};
  }
`;

const AccordionHeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 20px;
  width: 100%;
  font-size: ${tokens.typography.fontSize.xs};
`;

const Title = styled.div`
  width: 100%;
  padding: ${tokens.spacing.accordionPadding};
  font-size: ${tokens.typography.fontSize.xs};
  font-weight: ${tokens.typography.fontWeight.bold};
  text-transform: uppercase;
`;

const ExpandIcon = styled.div`
  padding: 0.4em 0.7em;
  font-size: 0.9375rem;
`;

const AccordionItem = styled.div`
  overflow: hidden;
  transition: ${props => props.collapsed ? tokens.transitions.accordionCollapse : tokens.transitions.accordion};
  height: ${props => props.collapsed ? '0' : 'auto'};
  max-height: ${props => props.collapsed ? '0' : '9999px'};
`;

const AccordionContent = styled.div.attrs({
  'data-accordion-content': true
})`
  background-color: transparent;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${tokens.spacing.accordionContentPadding};
  font-size: ${tokens.typography.fontSize.xs};

  /* Override FormLabel font-size (Bootstrap's Form.Label has explicit font-size that overrides inheritance) */
  label,
  .form-label {
    font-size: inherit;
  }
`;

const AccordionShowMoreContent = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const MoreLessButton = styled.span`
  color: ${props => props.theme?.colors?.info || tokens.colors.info};
  font-size: ${tokens.typography.fontSize.sm};
`;

/* --- Bullet variant: line-item style with bullet, hairline rule, and +/- --- */
const BulletWrap = styled.div`
  & + & {
    border-top: 1px solid ${tokens.colors['gray-200']};
  }
`;

const BulletHeader = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 10px;
  padding: 8px 4px 8px 6px;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
`;

const BulletTitle = styled.span`
  flex: 1;
  font-size: ${tokens.typography.fontSize.xs};
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.primary};
  text-transform: none;
`;

const BulletToggleIcon = styled.span`
  flex-shrink: 0;
  color: ${tokens.colors.primary};
  font-size: 14px;
  display: flex;
  align-items: center;
`;

const BulletBody = styled.div`
  padding: 4px 6px ${tokens.spacing.sm} 22px;
  font-size: ${tokens.typography.fontSize.xs};
  text-transform: none;
  color: ${tokens.colors['gray-800']};

  label,
  .form-label {
    font-size: inherit;
    text-transform: none;
  }
`;

function BulletTogglePanel(props) {
    const [expand, setExpand] = useState(props.isExpand ? props.isExpand : false);

    useEffect(() => {
        setExpand(props.isExpand);
    }, [props.isExpand]);

    const handleOnClick = (value) => {
        setExpand(value);
        props.handleOnClick(value);
    };

    return (
        <BulletWrap>
            <BulletHeader type="button" onClick={() => handleOnClick(!expand)} aria-expanded={expand}>
                <BulletTitle>{props.title}</BulletTitle>
                <BulletToggleIcon aria-hidden>
                    <FontAwesomeIcon icon={expand ? faMinusCircle : faPlusCircle} />
                </BulletToggleIcon>
            </BulletHeader>
            {expand && (
                <BulletBody
                    style={{
                        maxHeight: props.contentHeight ? props.contentHeight : undefined,
                    }}
                >
                    {props.children}
                </BulletBody>
            )}
        </BulletWrap>
    );
}

/* --- Wireframe sidebar (nested under explore filter rail): flat rows, no gray slabs --- */
const WfWrap = styled.div`
  & + & {
    border-top: 1px solid ${tokens.colors['gray-200']};
  }
`;

const WfHeader = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  gap: ${tokens.spacing.xs};
  padding: 6px 4px 6px 2px;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  color: ${APP_COLORS['tech-blue']};
  font-size: ${tokens.typography.fontSize.xs};
  font-weight: ${tokens.typography.fontWeight.semibold};
  letter-spacing: 0.04em;

  &:hover {
    color: ${tokens.colors['gray-900']};
  }
`;

const WfTitle = styled.span`
  flex: 1;
  text-transform: none;
`;

const WfChevron = styled.span`
  flex-shrink: 0;
  font-size: 10px;
  opacity: 0.85;
  width: 14px;
  text-align: center;
`;

const WfBody = styled.div`
  padding: 0 2px ${tokens.spacing.sm} 14px;
  font-size: ${tokens.typography.fontSize.xs};
  text-transform: none;
  color: ${tokens.colors['gray-800']};

  label,
  .form-label {
    font-size: inherit;
    text-transform: none;
  }
`;

function WireframeTogglePanel(props) {
    const [expand, setExpand] = useState(props.isExpand ? props.isExpand : false);
    const [more, setMore] = useState(false);

    useEffect(() => {
        setExpand(props.isExpand);
    }, [props.isExpand]);

    const handleOnClick = (value) => {
        setExpand(value);
        props.handleOnClick(value);
    };

    const visibleChildren = props.children;
    const hideChildren = props.hideChildren;

    return (
        <WfWrap>
            <WfHeader type="button" onClick={() => handleOnClick(!expand)} aria-expanded={expand}>
                <WfTitle>{props.title}</WfTitle>
                <WfChevron aria-hidden>
                    <FontAwesomeIcon icon={expand ? faChevronDown : faChevronRight} />
                </WfChevron>
            </WfHeader>
            {expand ? (
                <WfBody
                    style={{
                        maxHeight: props.contentHeight ? props.contentHeight : undefined,
                    }}
                >
                    {visibleChildren}
                    {more && <div>{hideChildren}</div>}
                    {hideChildren && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: tokens.spacing.xs }}>
                            <LinkButton onClick={() => setMore(!more)}>
                                {more ? (
                                    <MoreLessButton>LESS</MoreLessButton>
                                ) : (
                                    <MoreLessButton>MORE</MoreLessButton>
                                )}
                            </LinkButton>
                        </div>
                    )}
                </WfBody>
            ) : null}
        </WfWrap>
    );
}

function TogglePanel(props) {
    const [expand, setExpand] = useState(props.isExpand ? props.isExpand : false);
    const [more, setMore] = useState(false);

    useEffect(() => {
        setExpand(props.isExpand);
    }, [props.isExpand]);

    const handleOnClick = (value) => {
        setExpand(value);
        props.handleOnClick(value);
    };
    const visibleChildren = props.children;
    const hideChildren = props.hideChildren;

    if (props.variant === 'wireframe') {
        return <WireframeTogglePanel {...props} />;
    }

    if (props.variant === 'bullet') {
        return <BulletTogglePanel {...props} />;
    }

    return (
        <AccordionWrapper>
            <AccordionTitle
                className={classNames({
                    "open": expand,
                })}
            >
                <AccordionHeaderContent
                    onClick={() => handleOnClick(!expand)}
                >
                    <Title>{props.title}</Title>
                </AccordionHeaderContent>
                <ExpandIcon
                    onClick={() => handleOnClick(!expand)}
                >
                    { expand ?
                        <FontAwesomeIcon icon={faMinusCircle} />
                        :
                        <FontAwesomeIcon icon={faPlusCircle} />
                    }
                </ExpandIcon>
            </AccordionTitle>
            <AccordionItem collapsed={!expand}>
                <AccordionContent
                    style={{
                        maxHeight: props.contentHeight ? props.contentHeight : 'auto',
                    }}
                >
                    {visibleChildren}
                    {more &&
                        <div>
                            {hideChildren}
                        </div>
                    }
                    {hideChildren &&
                        <AccordionShowMoreContent>
                            <LinkButton
                                onClick={() => setMore(!more)}
                            >
                                {more ?
                                    <MoreLessButton>LESS</MoreLessButton>
                                    :
                                    <MoreLessButton>MORE</MoreLessButton>
                                }
                            </LinkButton>
                        </AccordionShowMoreContent>
                    }
                </AccordionContent>
            </AccordionItem>
        </AccordionWrapper>
    );
}

TogglePanel.propTypes = {
    contentHeight: PropTypes.number,
    isExpand: PropTypes.bool,
    handleOnClick: PropTypes.func,
    hideChildren: PropTypes.object,
    /** Flat row style for explore filter sidebar nested groups */
    variant: PropTypes.oneOf(['default', 'wireframe', 'bullet']),
};

TogglePanel.defaultProps = {
    handleOnClick: (e) => e,
};

export default TogglePanel;
